import React, { useState, useContext, useCallback } from 'react';
import { Button, Stack, Typography, Box, CircularProgress } from '@mui/material';
import Modal from 'components/_layouts/Modal';
import { translate as t } from 'hooks/useTranslations';
import { DraftManualDetailContext } from '../context';
import { neutral } from '_lex/_foundations/phxColors';
import useDraftManual from '../../_data_sources/useDraftManual';
import { PolicyEditorContext } from '../PolicyEditor/context';
import { AppContext } from 'views/App/context';
import { PreviewContent } from './_components/PreviewContent';
import { ExportOptions } from './_components/ExportOptions';
import type { ExportModalProps } from './types';
import useManageExportManual from '../../_data_sources/useExportManual';
import {
  downloadFileFromUrl,
  downloadFileFromBase64,
  generateExportFileName,
  sanitizeForFilename,
} from '../../helpers/exportHelper';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import errorHelper from 'helpers/errorHelper';

const ExportModal: React.FC<ExportModalProps> = ({
  open,
  onClose,
  exportType,
  externalManual,
  node,
  showContentFilters,
  currentPolicyData,
}) => {
  const { hasPermission, currentOrganizationId } = useContext(AppContext);

  const hasAwbSubscription =
    hasPermission('awbEssentials', currentOrganizationId || '') ||
    hasPermission('awbPremium', currentOrganizationId || '');

  const queryClient = useQueryClient();
  const { exportManual } = useManageExportManual();

  let draftManualContext;
  try {
    draftManualContext = useContext(DraftManualDetailContext);
  } catch (error) {
    draftManualContext = null;
  }

  const contextManual = draftManualContext?.manual;
  const externalManualId = externalManual?.id;

  const { data: fetchedManualData, isLoading: isLoadingManual } = useDraftManual(
    externalManualId && !contextManual ? externalManualId : undefined,
    {
      enabled: !!(externalManualId && !contextManual && open),
    }
  );

  const manual = contextManual || fetchedManualData || externalManual;

  const [showEditLevelTags, setShowEditLevelTags] = useState(false);
  const [showAccreditationTags, setShowAccreditationTags] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ attempt: number; maxAttempts: number } | null>(null);

  let policyContext;
  try {
    policyContext = useContext(PolicyEditorContext);
  } catch (error) {
    policyContext = null;
  }

  const policyAccreditationTags = policyContext?.accreditationTags || {};

  const triggerAutoSavePolicy = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      document.body.dispatchEvent(new Event('auto-save-policy'));
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  }, []);

  const triggerAutoSavePreface = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      document.body.dispatchEvent(new Event('auto-save-preface'));
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  }, []);

  const handleExport = async () => {
    if (!manual?.id) {
      enqueueSnackbar(t('draftManuals.export.exportError'), { variant: 'error' });
      return;
    }

    setIsExporting(true);
    setExportProgress(null);

    try {
      // Handle auto-save for currentContent
      if (exportType === 'currentContent' && node) {
        const [nodeType, id] = node.split(':');
        if (nodeType === 'policy' && showContentFilters) {
          await triggerAutoSavePolicy();
          queryClient.invalidateQueries(['draft_policy', manual.id, id]);
        } else if (nodeType === 'preface' && showContentFilters) {
          await triggerAutoSavePreface();
          queryClient.invalidateQueries({
            queryKey: ['draft_preface', manual.id, id],
            exact: false,
          });
        }
      }

      let contentId: string | undefined;
      let fileName = '';
      let tocParam: boolean | undefined;

      switch (exportType) {
        case 'manual':
          fileName = generateExportFileName(manual.name || 'Manual', 'manual');
          tocParam = false;
          break;
        case 'tableOfContents':
          fileName = generateExportFileName(manual.name || 'Manual', 'tableOfContents');
          tocParam = true;
          break;
        case 'currentContent':
          if (node) {
            const [type, id] = node.split(':');
            contentId = id;

            let contentTitle = '';
            if (type === 'policy' && currentPolicyData?.title) {
              contentTitle = sanitizeForFilename(currentPolicyData.title);
            } else if (type === 'preface' && currentPolicyData?.title) {
              contentTitle = sanitizeForFilename(currentPolicyData.title);
            }

            fileName = generateExportFileName(
              manual.name || 'Manual',
              'currentContent',
              type as 'policy' | 'preface',
              contentTitle
            );
          }
          break;
      }

      await exportManual(
        {
          manualId: manual.id,
          contentId,
          exportType: 'PDF',
          showEditLevelTags,
          showAccreditationTags,
          toc: tocParam,
        },
        async (response) => {
          // Handle different response formats
          if (response.base64) {
            // Direct base64 response (used for single content exports)
            downloadFileFromBase64({
              base64Content: response.base64,
              fileName,
              fileType: response.fileType || 'PDF',
            });
          } else if (response.presignedUrl) {
            // Presigned URL response (used for full manual exports)
            await downloadFileFromUrl({
              presignedUrl: response.presignedUrl,
              fileName,
              fileType: 'PDF',
              onProgress: (attempt, maxAttempts) => {
                setExportProgress({ attempt, maxAttempts });
              },
            });
          } else {
            throw new Error('Invalid export response format');
          }
          enqueueSnackbar(t('draftManuals.export.exportSuccess'), { variant: 'success' });
          setIsExporting(false);
          setExportProgress(null);
          onClose();
        },
        (error) => {
          console.error('Export error:', error);
          errorHelper.enqueueErrors(error);
          setIsExporting(false);
          setExportProgress(null);
        }
      );
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage =
        error instanceof Error && error.message === 'Time out has been exceeded'
          ? 'Time out has been exceeded'
          : t('draftManuals.export.exportError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const getButtonText = () => {
    if (isExporting) {
      return t('draftManuals.export.exporting', 'Exporting...');
    }
    return t('draftManuals.export.exportPdf');
  };

  const getStatusMessage = () => {
    if (isExporting && exportProgress) {
      return `${t('draftManuals.export.exporting', 'Exporting manual...')}`;
    }
    if (isExporting) {
      return t('draftManuals.export.exporting', 'Exporting manual...');
    }
    return null;
  };

  const actions = (
    <>
      <Button
        onClick={onClose}
        variant='outlined'
        disabled={isExporting}
      >
        {t('draftManuals.export.cancel')}
      </Button>
      <Button
        onClick={handleExport}
        variant='contained'
        disabled={isExporting}
      >
        {getButtonText()}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={isExporting ? () => {} : onClose}
      size='md'
      title={manual?.name || t('draftManuals.export.manual')}
      subtitle={t('draftManuals.export.title')}
      actions={actions}
    >
      <Box>
        {isExporting ? (
          <Stack
            spacing={2}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              py: 4,
            }}
          >
            <CircularProgress />
            <Typography variant='body1'>{getStatusMessage()}</Typography>
          </Stack>
        ) : (
          <>
            <ExportOptions
              showEditLevelTags={showEditLevelTags}
              setShowEditLevelTags={setShowEditLevelTags}
              showAccreditationTags={showAccreditationTags}
              setShowAccreditationTags={setShowAccreditationTags}
              exportType={exportType}
              hasAwbSubscription={hasAwbSubscription}
            />
            <Stack
              spacing={1}
              sx={{ flex: 1 }}
            >
              <Typography variant='subtitle2'>{t('draftManuals.export.preview')}</Typography>

              <Box
                sx={{
                  border: `1px solid ${neutral[200]}`,
                  borderRadius: 1,
                  backgroundColor: 'white',
                  minHeight: 300,
                  maxHeight: 350,
                  p: 2,
                  overflow: 'auto',
                }}
              >
                {isLoadingManual ? (
                  <CircularProgress />
                ) : (
                  <PreviewContent
                    exportType={exportType}
                    manual={manual}
                    showEditLevelTags={showEditLevelTags}
                    showAccreditationTags={showAccreditationTags}
                    policyAccreditationTags={policyAccreditationTags}
                  />
                )}
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ExportModal;
export type { ExportType, ExportConfig } from './types';
